/**
 * @file HistoryVersionSheet.tsx
 * @description Боковая панель версии вида сайта (phase19.16 Р1;
 * uiux-design-r1 §6): изменения относительно предыдущей версии, «Посмотреть
 * в предпросмотре», «Вернуть» (или объяснение, почему вернуть нельзя). На
 * телефоне — на весь экран.
 */

import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import { StatusPill } from "../../ui/StatusPill";
import { ChangeItemsList } from "../ChangeItemsList";
import { siteView } from "../../../lib/api-site-view";
import { siteViewErrorText } from "../../../lib/site-view/errors";
import { historyAuthor, historySummary, historyWhen } from "../../../lib/site-view/history";
import type { SiteHistoryItem, SiteHistoryVersion } from "../../../lib/site-view/types";

interface Props {
  item: SiteHistoryItem | null;
  onClose: () => void;
  onPreview: (item: SiteHistoryItem) => void;
  onRollback: (item: SiteHistoryItem) => void;
}

export function HistoryVersionSheet({ item, onClose, onPreview, onRollback }: Props) {
  const [detail, setDetail] = useState<SiteHistoryVersion | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDetail(null);
    setError(null);
    if (!item) return;
    siteView
      .historyVersion(item.version)
      .then(setDetail)
      .catch((err: unknown) => setError(siteViewErrorText(err, "Не удалось загрузить изменения версии.")));
  }, [item]);

  return (
    <Sheet open={item !== null} onOpenChange={(open) => (!open ? onClose() : null)}>
      <SheetContent side="right" className="w-full max-w-none overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Версия {item?.version}</SheetTitle>
        </SheetHeader>
        {item ? (
          <div className="flex flex-col gap-3">
            <p className="type-body text-on-surface">
              {historyWhen(item)} · {historyAuthor(item)}
            </p>
            <p className="type-body text-on-surface-variant">{historySummary(item)}</p>
            {item.isCurrent ? <StatusPill tone="success">На сайте</StatusPill> : null}
            {/* Р3г (SB135): здесь сменились условия согласия на cookie — посетителей спросили заново, версия хранится 3 года. */}
            {item.consentVersion === item.version ? (
              <p className="type-caption text-on-surface-variant">В этой версии сменились условия согласия на cookie — посетителей спросили заново. Версия хранится 3 года.</p>
            ) : null}
            {error ? <p className="type-body text-destructive">{error}</p> : null}
            {detail ? (
              detail.diff.length > 0 ? (
                <ChangeItemsList items={detail.diff} />
              ) : (
                <p className="type-body text-on-surface-variant">Отличий от предыдущей версии нет.</p>
              )
            ) : !error ? (
              <p className="type-body text-on-surface-variant">Загружаем изменения…</p>
            ) : null}
            {!item.isCurrent && !item.canRollback ? (
              <p className="type-caption text-on-surface-variant">
                Вернуть версию может владелец магазина: она затрагивает оформление и меню.
              </p>
            ) : null}
          </div>
        ) : null}
        <div className="mt-auto grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => item && onPreview(item)}>
            Посмотреть
          </Button>
          {item && !item.isCurrent && item.canRollback ? (
            <Button variant="brand" onClick={() => onRollback(item)}>
              Вернуть
            </Button>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
